import { FC, useEffect, useRef, useState } from 'react';

import { CREATE_MENU_PAGE_ROUTE, MULTI_CREATION_ROUTE } from 'utils/constants/app';

import { BottomSheet, Button, FloatingButton } from '@hh.ru/magritte-ui';
import { CopyOutlinedSize24, PlusOutlinedSize24 } from '@hh.ru/magritte-ui/icon';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import styles from 'components/CampaignsDashboard/components/CampaignCreationFloatingButton/CampaignCreationFloatingButton.module.less';

const TrlKeys = {
    create: 'CampaignsDashboard.CampaignsTabs.createButton',
    multipleCreate: 'CampaignsDashboard.CampaignCreationFloatingButton.multipleCreate',
};

export const CampaignCreationFloatingButton: FC = () => {
    const scrollTriggerRef = useRef<HTMLDivElement>(null);
    const [isCompact, setIsCompact] = useState(false);
    const [isBottomSheetVisible, setIsBottomSheetVisibleOn, setIsBottomSheetVisibleOff] = useOnOffState(false);
    const trls = useTrls();
    const createButtonLabel = trls(TrlKeys.create);

    useEffect(() => {
        const scrollTrigger = scrollTriggerRef.current;
        if (!scrollTrigger || !('IntersectionObserver' in window)) return undefined;

        const observer = new IntersectionObserver(([entry]) => setIsCompact(!entry.isIntersecting));
        observer.observe(scrollTrigger);

        return () => observer.disconnect();
    }, []);

    return (
        <>
            <div ref={scrollTriggerRef} className={styles.scrollTrigger} />
            <FloatingButton
                visible={!isBottomSheetVisible}
                style="neutral"
                position="right"
                borderRadius="halfHeight"
                icon={PlusOutlinedSize24}
                hideLabel={isCompact}
                className={styles.floatingButton}
                onClick={setIsBottomSheetVisibleOn}
                data-qa="campaigns-mobile-create-floating-button"
            >
                {createButtonLabel}
            </FloatingButton>
            <BottomSheet
                visible={isBottomSheetVisible}
                onClose={setIsBottomSheetVisibleOff}
                height="content"
                withContentPaddings={false}
                data-qa="campaigns-mobile-create-bottom-sheet"
            >
                <div className={styles.actions}>
                    <Button
                        Element={SPALink}
                        to={CREATE_MENU_PAGE_ROUTE}
                        mode="tertiary"
                        style="neutral"
                        size="large"
                        stretched
                        icon={<PlusOutlinedSize24 />}
                        onClick={setIsBottomSheetVisibleOff}
                        data-qa="campaigns-mobile-create-action"
                    >
                        {createButtonLabel}
                    </Button>
                    <Button
                        Element={SPALink}
                        to={MULTI_CREATION_ROUTE}
                        mode="tertiary"
                        style="neutral"
                        size="large"
                        stretched
                        icon={<CopyOutlinedSize24 />}
                        onClick={setIsBottomSheetVisibleOff}
                        data-qa="campaigns-mobile-multiple-create-action"
                    >
                        {trls(TrlKeys.multipleCreate)}
                    </Button>
                </div>
            </BottomSheet>
        </>
    );
};
